import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Card, InputAdornment, Stack, TextField } from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGridCustom from 'src/components/data-grid-custom';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { useDebounce } from 'src/hooks/use-debounce';
import { makeBenefitsApi } from 'src/http/api/internal/internal-http';
import { ReportsRepository } from 'src/repositories/benefits/reports.repository';
import { reportsitemType, ReportsListResponse } from 'src/types/reports';

type PaginatedReportsTabProps = {
  reportType?: string;
  unifiedBenefits?: boolean;
  showReportType?: boolean;
  withoutCard?: boolean;
  benefitCountLabel: string;
  errorLabel: string;
};

const reportsRepository = new ReportsRepository(makeBenefitsApi());

const EMPTY_REPORTS_LIST: ReportsListResponse = {
  data: [],
  total: 0,
  totalFiltered: 0,
  returnedCount: 0,
  page: 0,
  pageSize: 10,
  totalPages: 0,
};

const formatDateTime = (value: string) => {
  const date = new Date(value);

  return value && !Number.isNaN(date.getTime()) ? date.toLocaleString('pt-BR') : '-';
};

const mapStatusLabel = (status: string) => {
  const statusMap: Record<string, string> = {
    awaiting: 'Aguardando',
    pending: 'Pendente',
    processing: 'Processando',
    generating: 'Gerando',
    in_progress: 'Em progresso',
    success: 'Concluído',
    completed: 'Concluído',
    done: 'Concluído',
    failed: 'Falhou',
    error: 'Erro',
    cancelled: 'Cancelado',
  };

  return statusMap[status.toLowerCase().trim()] || status;
};

const mapStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  const normalizedStatus = status.toLowerCase().trim();

  if (['success', 'completed', 'done'].includes(normalizedStatus)) return 'success';
  if (['pending', 'processing', 'generating', 'in_progress'].includes(normalizedStatus)) {
    return 'warning';
  }
  if (['failed', 'error'].includes(normalizedStatus)) return 'error';
  if (normalizedStatus === 'cancelled') return 'info';
  return 'default';
};

const mapReportTypeLabel = (type: string) => {
  const reportTypeMap: Record<string, string> = {
    coupon: 'Cupom',
    product: 'Produto',
    'benefits-unified': 'Cupons & Produtos',
  };

  return reportTypeMap[type] || type;
};

export default function PaginatedReportsTab({
  reportType,
  unifiedBenefits = false,
  showReportType = false,
  withoutCard = false,
  benefitCountLabel,
  errorLabel,
}: PaginatedReportsTabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [reportsList, setReportsList] = useState<ReportsListResponse>(EMPTY_REPORTS_LIST);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        source: unifiedBenefits ? 'unified-benefits' : reportType,
        search: debouncedSearchText,
        sort: sortModel[0] ?? null,
      }),
    [debouncedSearchText, reportType, sortModel, unifiedBenefits]
  );
  const previousRequestKeyRef = useRef(requestKey);

  const getReportsList = useCallback(async () => {
    setIsLoadingReports(true);

    try {
      const params = {
        search: debouncedSearchText,
        sort: JSON.stringify(sortModel[0] ?? null),
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      };
      const response = unifiedBenefits
        ? await reportsRepository.getUnifiedBenefitsReportsList(params)
        : await reportsRepository.getPaginatedReportsList({
            ...params,
            type: reportType || '',
          });

      if (response.isLeft()) {
        throw new Error(response.value.message);
      }

      setReportsList({
        ...EMPTY_REPORTS_LIST,
        ...response.value,
        data: response.value?.data || [],
      });
    } catch (error: any) {
      enqueueSnackbar(error?.message || `Erro ao carregar relatórios de ${errorLabel}.`, {
        variant: 'error',
      });
      setReportsList(EMPTY_REPORTS_LIST);
    } finally {
      setIsLoadingReports(false);
    }
  }, [
    debouncedSearchText,
    enqueueSnackbar,
    errorLabel,
    paginationModel,
    reportType,
    sortModel,
    unifiedBenefits,
  ]);

  useEffect(() => {
    if (previousRequestKeyRef.current !== requestKey) {
      previousRequestKeyRef.current = requestKey;

      if (paginationModel.page !== 0) {
        setPaginationModel((current) => ({ ...current, page: 0 }));
        return;
      }
    }

    getReportsList();
  }, [getReportsList, paginationModel.page, requestKey]);

  const rows = useMemo(
    () =>
      reportsList.data.map((report: reportsitemType, index) => ({
        ...report,
        id: report.id || report._id || `${reportsList.page}-${index}`,
      })),
    [reportsList.data, reportsList.page]
  );

  const columns = useMemo<GridColDef[]>(() => {
    const reportColumns: GridColDef[] = [
      { field: 'user', headerName: 'Usuário', flex: 1, minWidth: 200, sortable: false },
      {
        field: 'createdAt',
        headerName: 'Data de geração',
        flex: 1,
        minWidth: 190,
        valueFormatter: ({ value }) => formatDateTime(String(value || '')),
      },
      {
        field: 'benefitCount',
        headerName: benefitCountLabel,
        flex: 0.9,
        minWidth: 210,
        sortable: false,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          const status = String(params.value || '');

          return status ? (
            <Label color={mapStatusColor(status)} variant="soft">
              {mapStatusLabel(status)}
            </Label>
          ) : (
            '-'
          );
        },
      },
      {
        field: 'downloadLink',
        headerName: 'Link de download',
        flex: 1,
        minWidth: 190,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const link = String(params.value || '');

          return link ? (
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={link}
              target="_blank"
              rel="noreferrer"
              startIcon={<Iconify icon="solar:download-linear" width={16} />}
            >
              Baixar
            </Button>
          ) : (
            '-'
          );
        },
      },
    ];

    if (!showReportType) {
      return reportColumns;
    }

    return [
      {
        field: 'reportType',
        headerName: 'Tipo',
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        valueFormatter: ({ value }) => mapReportTypeLabel(String(value || '')),
      },
      ...reportColumns,
    ];
  }, [benefitCountLabel, showReportType]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    const nextSort = model[0];
    setSortModel(nextSort && ['createdAt', 'status'].includes(nextSort.field) ? [nextSort] : []);
  }, []);

  const toolbar = useMemo(
    () => (
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <TextField
          placeholder="Pesquisar por usuário..."
          variant="standard"
          size="small"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <LoadingButton
          size="small"
          variant="outlined"
          startIcon={<Iconify icon="solar:refresh-linear" width={16} />}
          onClick={getReportsList}
          loading={isLoadingReports}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          Atualizar
        </LoadingButton>
      </Stack>
    ),
    [getReportsList, isLoadingReports, searchText]
  );

  const table = (
    <Box height="100%">
      <DataGridCustom
        animatedEachRow
        customFilters={toolbar}
        disableColumnMenu
        excludeExportButton
        checkboxSelection={false}
        columns={columns}
        rows={rows}
        totalRowsCount={reportsList.totalFiltered ?? reportsList.total}
        filteredRowsCount={reportsList.totalFiltered ?? reportsList.total}
        initialVisibleColumns={{}}
        sortModel={sortModel}
        paginationModel={paginationModel}
        pageSizeOptions={[5, 10, 20, 30, 40, 50]}
        isLoading={isLoadingReports}
        setSortModel={handleSortModelChange}
        setPaginationModel={setPaginationModel}
        height={550}
      />
    </Box>
  );

  return withoutCard ? table : <Card sx={{ px: 2, pt: 1, pb: 0, boxShadow: 4 }}>{table}</Card>;
}
